import debounce from 'lodash/debounce'
import StyleWrapper from 'publisher/components/core/StyleWrapper'
import FieldErrors from 'publisher/components/FieldErrors'
import fieldValidation from 'publisher/utils/fieldValidation'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCalculateFormFieldStyles } from 'src/publisher/shared/hooks/use-calculate-form-fields-styles'
import { Field } from 'src/shared/components/field'
import { FieldIconNew } from 'src/shared/components/field-icon-new'
import { FieldWrapper } from 'src/shared/components/field-wrapper'
import { ContactUsFieldEntityInterface } from 'src/shared/entities/contact-us-field/contact-us-field-entity.schema'
import { FieldEntityInterface } from 'src/shared/entities/field/field-entity.schema'
import { FieldSubEntityInterface } from 'src/shared/schemas/fields/field-sub-entity.schema'

export interface BasePublisherBaseFieldProps {
  entity:
    | FieldEntityInterface
    | ContactUsFieldEntityInterface
    | FieldSubEntityInterface
  value: string | undefined
  onChange: (value: string | undefined) => void
  errors: string[]
  needInitialise?: boolean
  type?: string
  readOnly?: boolean
  disabled?: boolean
}

export const BasePublisherBaseField = ({
  entity,
  value,
  errors,
  onChange,
  needInitialise = true,
  type = 'text',
  readOnly,
  disabled,
}: BasePublisherBaseFieldProps) => {
  const [isClient, setIsClient] = useState(false)
  const [valueState, setValueState] = useState(value)
  const [inlineErrors, setInlineErrors] = useState([])
  const { t } = useTranslation()

  const updateReduxValue = useCallback(
    debounce((value?: string) => {
      onChange(value)
    }, 100),
    [],
  )

  const handleChange = useCallback(
    (value?: string) => {
      if (entity.slug) {
        setValueState(value)
        updateReduxValue(value)
      }
    },
    [updateReduxValue, entity.slug],
  )

  const handleBlur = useCallback(
    (value?: string) => {
      const errors = fieldValidation(entity.slug, value, entity.optional)
      setInlineErrors(errors.map((error: string) => t(error)))
      setTimeout(() => {
        window.dispatchEvent(new Event('form_updated'))
      }, 100)
    },
    [t, entity.slug, entity.optional],
  )

  const calculatedErrors = inlineErrors.length > 0 ? inlineErrors : errors

  // need to init all fields
  // biome-ignore lint/correctness/useExhaustiveDependencies: supposed to be fired once
  useEffect(() => {
    if (needInitialise) {
      // NOTE: we don't need debounce here
      onChange(value)
    }
  }, [needInitialise])

  useEffect(() => {
    if (value !== valueState) {
      setValueState(value)
    }
  }, [value])

  useEffect(() => {
    setIsClient(true)
  }, [])

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    handleChange(e.target.value)

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) =>
    handleBlur(e.target.value)

  const {
    calculatedBorder,
    calculatedPadding,
    calculatedFontSize,
    calculatedLineHeight,
    calculatedTextAlign,
    calculatedFontFamily,
    calculatedColor,
    calculatedBackgroundColor,
  } = useCalculateFormFieldStyles({
    border: entity.border,
    mobileBorder: entity.mobileBorder,
    padding: entity.padding,
    mobilePadding: entity.mobilePadding,
    fontSize: entity.fontSize,
    mobileFontSize: entity.mobileFontSize,
    lineHeight: entity.lineHeight,
    mobileLineHeight: entity.mobileLineHeight,
    textAlign: entity.textAlign,
    mobileTextAlign: entity.mobileTextAlign,
    color: entity.color,
    mobileColor: entity.mobileColor,
    backgroundColor: entity.backgroundColor,
    mobileBackgroundColor: entity.mobileBackgroundColor,
  })

  return (
    isClient && (
      <StyleWrapper
        margin={entity.margin}
        mobileMargin={entity.mobileMargin}
        appearance={entity.appearance}
      >
        <FieldWrapper>
          {entity.iconClassName && (
            <FieldIconNew
              color={entity.color || 'initial'}
              fontSize={calculatedFontSize.desktop}
              mobileFontSize={calculatedFontSize.mobile}
              mobileColor={entity.mobileColor || entity.color || 'initial'}
              className={entity.iconClassName}
            />
          )}
          <Field
            id={entity.htmlAttrId}
            fontFamily={calculatedFontFamily.desktop}
            mobileFontFamily={calculatedFontFamily.mobile}
            name={entity.slug}
            type={type}
            placeholder={entity.placeholder}
            padding={calculatedPadding.desktop}
            mobilePadding={calculatedPadding.mobile}
            lineHeight={calculatedLineHeight.desktop}
            mobileLineHeight={calculatedLineHeight.mobile}
            textAlign={calculatedTextAlign.desktop}
            mobileTextAlign={calculatedTextAlign.mobile}
            border={calculatedBorder.desktop}
            mobileBorder={calculatedBorder.mobile}
            backgroundColor={calculatedBackgroundColor.desktop}
            mobileBackgroundColor={calculatedBackgroundColor.mobile}
            placeholderColor={entity.placeholderColor}
            mobilePlaceholderColor={entity.mobilePlaceholderColor}
            onChange={onInputChange}
            onBlur={onInputBlur}
            color={calculatedColor.desktop}
            mobileColor={calculatedColor.mobile}
            fontSize={calculatedFontSize.desktop}
            mobileFontSize={calculatedFontSize.mobile}
            boxShadow={entity.boxShadow}
            mobileBoxShadow={entity.mobileBoxShadow}
            hasErrors={calculatedErrors.length > 0}
            disabled={disabled}
            value={valueState || ''}
            readOnly={readOnly}
          />
        </FieldWrapper>
        <FieldErrors errors={calculatedErrors} />
      </StyleWrapper>
    )
  )
}
